// Rules for choosing report dates in the calendar dialog.
// The draft is the range selected in the dialog but not yet applied to the report.
// Calendar clicks change that range; pointing at a period only calculates a preview
// of what a click there would do.
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import { findPeriodRangeForDates, moveNearestRangeBoundary, PeriodIndexRange } from './periodRanges.ts';

export interface DraftRange {
    // Positions in the available day, week, or month options. A null firstIndex means
    // nothing is selected; a null lastIndex means the user still needs to choose an end.
    firstIndex: number | null;
    lastIndex: number | null;
}

type ClickSelection =
    | { needsBoundaryChoice: true }
    | { needsBoundaryChoice: false; range: DraftRange };

export function selectDraftPeriod(draft: DraftRange, periodIndex: number): ClickSelection {
    if (draft.firstIndex == null) {
        return {
            needsBoundaryChoice: false,
            range: {
                firstIndex: periodIndex,
                lastIndex: null,
            },
        };
    }

    if (draft.lastIndex == null) {
        return {
            needsBoundaryChoice: false,
            range: {
                firstIndex: Math.min(draft.firstIndex, periodIndex),
                lastIndex: Math.max(draft.firstIndex, periodIndex),
            },
        };
    }

    const isInsideRange = periodIndex > draft.firstIndex && periodIndex < draft.lastIndex;
    if (isInsideRange) return { needsBoundaryChoice: true };

    const [firstIndex, lastIndex] = moveNearestRangeBoundary(
        draft.firstIndex,
        draft.lastIndex,
        periodIndex,
    );
    return {
        needsBoundaryChoice: false,
        range: {
            firstIndex,
            lastIndex,
        },
    };
}

export function mapDraftToResolution(
    draft: DraftRange,
    oldPeriods: PeriodOption[],
    newPeriods: PeriodOption[],
): DraftRange {
    if (draft.firstIndex == null) return { firstIndex: null, lastIndex: null };
    const oldFirst = oldPeriods[draft.firstIndex];
    const oldLast = oldPeriods[draft.lastIndex ?? draft.firstIndex];
    const mapped = findPeriodRangeForDates(newPeriods, oldFirst.start, oldLast.end);
    if (!mapped) return { firstIndex: null, lastIndex: null };
    // If the user has only clicked a start date, switching from days to weeks/months
    // must still leave the end unselected, even though the new start covers a whole period.
    const mappedDraft = draft.lastIndex == null
        ? { firstIndex: mapped.firstIndex, lastIndex: null }
        : mapped;
    return mappedDraft;
}

export function hasRangeChanged(
    draft: DraftRange,
    periods: PeriodOption[],
    resolution: Resolution,
    initialFirst: PeriodOption,
    initialLast: PeriodOption,
    initialResolution: Resolution,
): boolean {
    if (resolution !== initialResolution) return true;
    if (draft.firstIndex == null || draft.lastIndex == null) return true;
    const hasChangedDates = periods[draft.firstIndex]?.start.getTime() !== initialFirst.start.getTime()
        || periods[draft.lastIndex]?.start.getTime() !== initialLast.start.getTime();
    return hasChangedDates;
}

export function toCompleteRange(draft: DraftRange): PeriodIndexRange | null {
    if (draft.firstIndex == null || draft.lastIndex == null) return null;
    return { firstIndex: draft.firstIndex, lastIndex: draft.lastIndex };
}

export function toVisualRange(draft: DraftRange): PeriodIndexRange | null {
    if (draft.firstIndex == null) return null;
    return {
        firstIndex: draft.firstIndex,
        lastIndex: draft.lastIndex ?? draft.firstIndex,
    };
}

export function toDraftRange(range: PeriodIndexRange | null, fallback: DraftRange): DraftRange {
    if (!range) return fallback;
    return { firstIndex: range.firstIndex, lastIndex: range.lastIndex };
}

export function getHoverRange(draft: DraftRange, targetIndex: number): PeriodIndexRange {
    if (draft.firstIndex == null) return { firstIndex: targetIndex, lastIndex: targetIndex };
    if (draft.lastIndex == null) {
        return {
            firstIndex: Math.min(draft.firstIndex, targetIndex),
            lastIndex: Math.max(draft.firstIndex, targetIndex),
        };
    }
    if (targetIndex > draft.firstIndex && targetIndex < draft.lastIndex) {
        // Clicking between the selected start and end will ask which date to replace.
        // Until that choice is made, keep highlighting the existing range.
        return { firstIndex: draft.firstIndex, lastIndex: draft.lastIndex };
    }
    const [firstIndex, lastIndex] = moveNearestRangeBoundary(
        draft.firstIndex,
        draft.lastIndex,
        targetIndex,
    );
    return { firstIndex, lastIndex };
}

export function getHoverIntent(draft: DraftRange, targetIndex: number): string {
    if (draft.firstIndex == null) return 'Click to set start';
    if (draft.lastIndex == null) {
        if (targetIndex < draft.firstIndex) return 'Click to set start';
        if (targetIndex > draft.firstIndex) return 'Click to set end';
        return 'Click to select this period';
    }
    if (targetIndex === draft.firstIndex || targetIndex === draft.lastIndex) {
        return 'Click to select only this period';
    }
    if (targetIndex < draft.firstIndex) return 'Click to set start';
    if (targetIndex > draft.lastIndex) return 'Click to set end';
    return 'Click to choose start or end';
}
